import { cn } from "@/ds/utils";
import { usePreferredCurrency } from "@/features/common/api/usePreferredCurrency";
import type { Currency } from "@/lib/domain/Currency";
import type { Money } from "@/lib/domain/Money";
import { buildDisplayOutput } from "@/ds/custom/MoneyDisplayLogic";
import type { MoneyDisplayFormat } from "@/ds/custom/MoneyDisplayLogic";

type SharedProps = {
	className?: string;
	money: Money;
} & ({
	mode: "DeltaPercentage";
	originalValue: number;
} | {
	mode: Exclude<MoneyDisplayFormat, "DeltaPercentage">;
});

export type MoneyDisplayProps = SharedProps & { preferredCurrency?: Currency };
type ApiMoneyDisplayProps = SharedProps;
type RenderedMoneyDisplayProps = SharedProps & { preferredCurrency: Currency };

export const MoneyDisplay = (props: MoneyDisplayProps): React.JSX.Element => {
	return (
		props.preferredCurrency
			? <RenderedMoneyDisplay preferredCurrency={props.preferredCurrency} {...props} />
			: <ApiMoneyDisplay {...props} />
	);
};

const ApiMoneyDisplay = (props: ApiMoneyDisplayProps): React.JSX.Element => {
	const preferredCurrency = usePreferredCurrency();
	const currency = preferredCurrency.data ? preferredCurrency.data.currency : "USD";

	return (
		<RenderedMoneyDisplay preferredCurrency={currency} {...props} />
	);
};

const RenderedMoneyDisplay = (props: RenderedMoneyDisplayProps): React.JSX.Element => {
	const originalValue = props.mode === "DeltaPercentage" ? props.originalValue : undefined;
	const { text, className } = buildDisplayOutput(props.money, props.preferredCurrency, props.mode, originalValue, props.className);

	return (
		<div className={cn("whitespace-nowrap overflow-hidden text-ellipsis", className)}>
			{text}
		</div>
	);
};
